import { BotController, getBotDefinition, listBotDefinitions, type BotStrategySnapshot } from './bot/bot';
import { extractPlayerMatchFeatures } from './bot/learning/feature-extractor';
import { hashPlayerModel, createEmptyPlayerLearningModel, dominantPlayerStyle, type PlayerLearningModel } from './bot/learning/types';
import { trainPlayerLearningModel } from './bot/learning/trainer';
import { ADAPTIVE_BOT_ID } from './bot/implementations/adaptive/adaptive-bot';
import { createGameContent, DEFAULT_BOT_DECK, DEFAULT_PLAYER_DECK } from './content/catalog';
import { createDiagnosticBundle, type MatchDiagnosticBundle } from './diagnostics/analyzer';
import { BattleEngine, DEFAULT_RULES, ENGINE_VERSION } from './engine/engine';
import { elixirStageAt } from './engine/battle-rules';
import type { BattleCommand, BattleEvent, CardId, PlayerId, WorldPoint } from './engine/types';
import { playReplay } from './replay/playback';
import type { GameRenderer } from './renderer/game-renderer';
import { CARD_IMAGES } from './renderer/assets';
import { GameAudio } from './audio/audio';
import { clearDiagnosticHistory, clearDiagnosticUploadQueue, deleteDiagnosticBundle, downloadDiagnosticBundle, loadLatestDiagnosticBundle, saveDiagnosticBundle } from './storage/diagnostics-store';
import { parseSavedSettings, saveSettings, type AppSettings } from './app/settings';
import { DECK_STORAGE_KEY, parseSavedDeckSelection, saveDeckSelection, type DeckSelection } from './storage/deck-store';
import { BOT_PREFERENCES_STORAGE_KEY, parseBotPreferences, saveBotPreferences, type BotPreferences } from './storage/bot-preferences-store';
import { clearPlayerLearningModel, loadPlayerLearningModel, savePlayerLearningModel } from './storage/player-model-store';
import { normalizePlayerName, parsePlayerProfile, PLAYER_PROFILE_STORAGE_KEY, savePlayerProfile, type PlayerProfile } from './storage/player-profile-store';
import { clearStartupRecovery, consumeStartupRetry, scheduleStartupReload } from './app/startup-recovery';
import { AnalyticsClient } from './analytics/client';
import { MatchAnalyticsAggregator } from './analytics/contract';
import { DiagnosticUploader } from './analytics/diagnostic-uploader';
import { loadAnalyticsIdentity } from './analytics/identity';
import { SessionTracker } from './analytics/session-tracker';

const $ = <T extends HTMLElement>(selector: string): T => {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing UI element ${selector}`);
  return element;
};

const content = createGameContent();
const versionLabel = $('#versionLabel');
versionLabel.textContent = `ENGINE ${ENGINE_VERSION} · ${content.cards.size} КАРТ`;
versionLabel.dataset.engineVersion = ENGINE_VERSION;
versionLabel.dataset.contentVersion = content.version;
const defaultDecks = { player: DEFAULT_PLAYER_DECK, bot: DEFAULT_BOT_DECK } as const;
const parsedDecks = parseSavedDeckSelection(localStorage.getItem(DECK_STORAGE_KEY), new Set([...content.cards.keys()]), defaultDecks);
let deckSelection: DeckSelection = parsedDecks.selection;
let deckEditorOwner: PlayerId = 'player';
let selectedDeckSlot = 0;
const botDefinitions = listBotDefinitions();
const parsedBotPreferences = parseBotPreferences(localStorage.getItem(BOT_PREFERENCES_STORAGE_KEY), new Set(botDefinitions.map((definition) => definition.id)), ADAPTIVE_BOT_ID);
let botPreferences: BotPreferences = parsedBotPreferences.preferences;
let playerLearningModel: PlayerLearningModel = createEmptyPlayerLearningModel(content.version);
let activeBotStrategy: BotStrategySnapshot | null = null;
let learningEligible = false;
const fastMode = new URLSearchParams(location.search).has('fast');
const activeRules = fastMode
  ? {
      ...DEFAULT_RULES,
      countdownTicks: 15,
      normalTicks: 300,
      overtimeTicks: 60,
      singleElixirPerTickMilli: 80,
      doubleElixirPerTickMilli: 160,
      tripleElixirPerTickMilli: 240,
      normalDoubleElixirStartTick: 200,
      overtimeTripleElixirStartTick: 30,
    }
  : DEFAULT_RULES;
const settings = parseSavedSettings(localStorage.getItem('towerarena.com.settings'));
let playerProfile: PlayerProfile = parsePlayerProfile(localStorage.getItem(PLAYER_PROFILE_STORAGE_KEY));
const analyticsIdentity = loadAnalyticsIdentity(localStorage, sessionStorage);
const analytics = new AnalyticsClient(analyticsIdentity, () => settings.shareAnonymousMetrics);
const sessionTracker = new SessionTracker({
  identity: analyticsIdentity,
  engineVersion: ENGINE_VERSION,
  contentVersion: content.version,
  visible: document.visibilityState === 'visible',
  storage: sessionStorage,
});
const diagnosticUploader = new DiagnosticUploader(analyticsIdentity, () => settings.uploadDiagnosticLogs);
const audio = new GameAudio(settings);
let engine: BattleEngine | null = null;
let bot: BotController | null = null;
let renderer: GameRenderer;
let rendererInitialization: Promise<void> | null = null;
let matchStarting = false;
let running = false;
let paused = false;
let selectedCard: CardId | null = null;
let draggingCard: CardId | null = null;
let lastEvents: readonly BattleEvent[] = [];
let lastBundle: MatchDiagnosticBundle | null = null;
let finishing = false;
let replayCommands: readonly BattleCommand[] | null = null;
let replayIndex = 0;
let toastTimer = 0;
let debugOpen = false;
let handRenderKey = '';
let selectedEntityId: number | null = null;
let measuredFps = 0;
let fpsFrames = 0;
let fpsWindowStart = performance.now();
let resumeAfterSettings = false;
let clearLogsArmedUntil = 0;
let deleteResultLogArmedUntil = 0;
let deleteServerLogsArmedUntil = 0;
let frameDropBurst: { count: number; maxFrameMs: number; totalFrameMs: number; firstTick: number; lastTick: number } | null = null;
let lastFrameDropFlush = performance.now();
let matchAnalytics: MatchAnalyticsAggregator | null = null;
let matchStartedAt = '';
let currentMatchIsReplay = false;
let currentMatchReportedAbandoned = false;

const ui = {
  start: $('#startScreen'), result: $('#resultScreen'), settings: $('#settingsScreen'), hand: $('#hand'), next: $('#nextCard') as HTMLImageElement,
  timer: $('#timer'), phase: $('#phase'), playerCrowns: $('#playerCrowns'), botCrowns: $('#botCrowns'), elixirFill: $('#elixirFill'), elixirValue: $('#elixirValue'),
  countdown: $('#countdown'), toast: $('#toast'), pause: $('#pauseButton') as HTMLButtonElement, debugPanel: $('#debugPanel'), debugText: $('#debugText'),
  resultBadge: $('#resultBadge'), resultTitle: $('#resultTitle'), resultSummary: $('#resultSummary'), diagnosticCounts: $('#diagnosticCounts'), findings: $('#findings'),
  export: $('#exportButton') as HTMLButtonElement, replay: $('#replayButton') as HTMLButtonElement, deleteLog: $('#deleteLogButton') as HTMLButtonElement,
  play: $('#playButton') as HTMLButtonElement, lastBattle: $('#lastBattleButton') as HTMLButtonElement,
  playerNameInput: $('#playerNameInput') as HTMLInputElement, playerNameLabel: $('#playerNameLabel'),
  playerDeckTab: $('#playerDeckTab') as HTMLButtonElement, botDeckTab: $('#botDeckTab') as HTMLButtonElement,
  deckTitle: $('#deckTitle'), deckAverage: $('#deckAverage'), deckSlots: $('#deckSlots'), cardCatalog: $('#cardCatalog'),
  botType: $('#botTypeSelect') as HTMLSelectElement, botLearningStatus: $('#botLearningStatus'), botStrategySummary: $('#botStrategySummary'), resetBotLearning: $('#resetBotLearningButton') as HTMLButtonElement,
};

function syncPlayerName(): void {
  ui.playerNameInput.value = playerProfile.displayName;
  ui.playerNameLabel.textContent = playerProfile.displayName;
  ui.playerNameLabel.title = playerProfile.displayName;
}

function commitPlayerName(notifyOnFailure = true): void {
  playerProfile = { schemaVersion: 1, displayName: normalizePlayerName(ui.playerNameInput.value) };
  syncPlayerName();
  if (!savePlayerProfile(localStorage, playerProfile) && notifyOnFailure) showToast('Имя применено, но браузер не смог его сохранить');
}

function persistSettings(): void {
  if (!saveSettings(localStorage, settings)) showToast('Настройки применены, но браузер не смог их сохранить');
  document.body.classList.toggle('reduce-motion', settings.reducedMotion);
  audio.sync();
  if (renderer) renderer.setQuality(settings.quality);
}

function showToast(message: string): void {
  ui.toast.textContent = message;
  ui.toast.classList.add('show');
  toastTimer = 1.8;
}

function persistDeckSelection(): void {
  if (!saveDeckSelection(localStorage, deckSelection)) showToast('Колоды применены, но браузер не смог их сохранить');
  analytics.event('deck_saved', { owner: deckEditorOwner });
}

function persistBotPreferences(): void {
  if (!saveBotPreferences(localStorage, botPreferences)) showToast('Режим бота применён, но браузер не смог его сохранить');
}

function renderBotConfiguration(): void {
  ui.botType.replaceChildren();
  for (const definition of botDefinitions) {
    const option = document.createElement('option');
    option.value = definition.id;
    option.textContent = `${definition.name} · ${definition.version}`;
    option.selected = definition.id === botPreferences.botId;
    ui.botType.append(option);
  }
  const definition = getBotDefinition(botPreferences.botId);
  const style = dominantPlayerStyle(playerLearningModel.style);
  ui.botLearningStatus.textContent = definition.supportsLearning
    ? `Обучение: ${playerLearningModel.matchCount} боёв · уверенность ${Math.round(playerLearningModel.confidence / 10)}%`
    : 'Обучение отключено для этого варианта бота';
  const lastSelection = activeBotStrategy?.botId === definition.id ? ` · последняя колода: ${activeBotStrategy.selectedDeck.join(', ')}` : '';
  ui.botStrategySummary.textContent = definition.supportsLearning
    ? playerLearningModel.matchCount > 0
      ? `Текущая модель: ${style} · hash ${hashPlayerModel(playerLearningModel)}${lastSelection}`
      : 'Истории пока нет — будет использована ручная колода'
    : `Колода и решения используют классическую детерминированную policy${lastSelection}`;
  ui.resetBotLearning.disabled = playerLearningModel.matchCount === 0;
}

function createBotStrategy(seed: number): { strategy: BotStrategySnapshot; definition: ReturnType<typeof getBotDefinition>; selection: ReturnType<ReturnType<typeof getBotDefinition>['selectDeck']> } {
  const definition = getBotDefinition(botPreferences.botId);
  const selection = definition.selectDeck({ seed, content, manualDeck: deckSelection.bot, model: playerLearningModel });
  const strategy: BotStrategySnapshot = Object.freeze({
    schemaVersion: 1,
    botId: definition.id,
    botVersion: definition.version,
    deckPolicyId: definition.deckPolicyId,
    deckPolicyVersion: definition.deckPolicyVersion,
    modelVersion: definition.supportsLearning ? playerLearningModel.modelVersion : null,
    modelHash: definition.supportsLearning ? hashPlayerModel(playerLearningModel) : 'none',
    confidence: definition.supportsLearning ? playerLearningModel.confidence : 0,
    sourceMatches: definition.supportsLearning ? playerLearningModel.matchCount : 0,
    style: definition.supportsLearning ? { ...playerLearningModel.style } : { ...createEmptyPlayerLearningModel(content.version).style },
    selectedDeck: [...selection.deck],
    deckScore: selection.score,
    rationale: [...selection.rationale],
  });
  return { strategy, definition, selection };
}

function renderDeckBuilder(): void {
  const deck = deckSelection[deckEditorOwner];
  ui.playerDeckTab.classList.toggle('active', deckEditorOwner === 'player');
  ui.botDeckTab.classList.toggle('active', deckEditorOwner === 'bot');
  ui.deckTitle.textContent = `${deckEditorOwner === 'player' ? 'МОЯ КОЛОДА' : 'КОЛОДА БОТА'} · ${deck.length}/8`;
  const totalCost = deck.reduce((sum, cardId) => sum + (content.cards.get(cardId)?.cost ?? 0), 0);
  ui.deckAverage.textContent = `Средний эликсир ${(totalCost / deck.length).toFixed(1)}`;
  ui.deckSlots.replaceChildren();
  deck.forEach((cardId, index) => {
    const card = content.cards.get(cardId)!;
    const button = document.createElement('button');
    button.className = `deck-slot${index === selectedDeckSlot ? ' selected' : ''}`;
    button.type = 'button';
    button.setAttribute('aria-label', `Слот ${index + 1}: ${card.name}`);
    const image = document.createElement('img');
    image.src = CARD_IMAGES[cardId] ?? '';
    image.alt = card.name;
    const number = document.createElement('small');
    number.textContent = String(index + 1);
    button.append(image, number);
    button.addEventListener('click', () => { selectedDeckSlot = index; renderDeckBuilder(); });
    ui.deckSlots.append(button);
  });
  ui.cardCatalog.replaceChildren();
  for (const card of [...content.cards.values()].sort((left, right) => left.cost - right.cost || left.id.localeCompare(right.id))) {
    const existingIndex = deck.indexOf(card.id);
    const button = document.createElement('button');
    button.className = `catalog-card${existingIndex >= 0 ? ' in-deck' : ''}`;
    button.type = 'button';
    button.setAttribute('aria-label', `${card.name}, ${card.cost} эликсира${existingIndex >= 0 ? ', уже в колоде' : ''}`);
    const image = document.createElement('img');
    image.src = CARD_IMAGES[card.id] ?? '';
    image.alt = card.name;
    const cost = document.createElement('b');
    cost.textContent = String(card.cost);
    const name = document.createElement('span');
    name.textContent = card.name;
    button.append(image, cost, name);
    button.addEventListener('click', () => {
      const current = deckSelection[deckEditorOwner];
      const alreadySelected = current.indexOf(card.id);
      if (alreadySelected >= 0) {
        selectedDeckSlot = alreadySelected;
      } else {
        const next = [...current];
        next[selectedDeckSlot] = card.id;
        deckSelection = { ...deckSelection, [deckEditorOwner]: next };
        selectedDeckSlot = (selectedDeckSlot + 1) % next.length;
        persistDeckSelection();
      }
      renderDeckBuilder();
    });
    ui.cardCatalog.append(button);
  }
}

function ensureRenderer(): Promise<void> {
  if (rendererInitialization) return rendererInitialization;
  const initialize = async (): Promise<void> => {
    const { GameRenderer } = await import('./renderer/game-renderer');
    renderer = new GameRenderer($('#gameHost'), () => engine?.journal ?? null);
    await Promise.all([
      renderer.init(),
      ...[...new Set(Object.values(CARD_IMAGES))].map(async (source) => {
        const image = new Image();
        image.src = source;
        try { await image.decode(); } catch { /* Renderer reports a missing card asset when it is used. */ }
      }),
    ]);
    renderer.setQuality(settings.quality);
  };
  rendererInitialization = initialize().catch((error: unknown) => {
    rendererInitialization = null;
    throw error;
  });
  return rendererInitialization;
}

async function startMatch(replayBundle?: MatchDiagnosticBundle): Promise<void> {
  commitPlayerName();
  if (matchStarting) return;
  matchStarting = true;
  // Must happen synchronously inside the user gesture, before renderer loading awaits.
  audio.unlock();
  ui.play.disabled = true;
  ui.play.textContent = 'ЗАГРУЗКА АРЕНЫ…';
  try {
    await ensureRenderer();
    await startReadyMatch(replayBundle);
  } catch (error) {
    audio.stopMusic();
    running = false;
    paused = false;
    bot = null;
    engine = null;
    ui.start.classList.add('open');
    ui.result.classList.remove('open');
    ui.pause.disabled = true;
    if (scheduleStartupReload(sessionStorage, error, replayBundle ? 'replay' : 'live')) {
      location.reload();
      return;
    }
    clearStartupRecovery(sessionStorage);
    showToast(`Не удалось запустить бой: ${String(error)}`);
    toastTimer = 6;
  } finally {
    matchStarting = false;
    ui.play.disabled = false;
    ui.play.textContent = 'ИГРАТЬ ПРОТИВ БОТА';
  }
}

async function startReadyMatch(replayBundle?: MatchDiagnosticBundle): Promise<void> {
  const startupStartedAt = performance.now();
  const seed = replayBundle?.replay.seed ?? (Date.now() >>> 0);
  const liveBot = replayBundle ? null : createBotStrategy(seed);
  activeBotStrategy = replayBundle?.replay.botStrategy ?? liveBot?.strategy ?? null;
  learningEligible = !replayBundle && Boolean(liveBot?.definition.supportsLearning);
  currentMatchIsReplay = Boolean(replayBundle);
  currentMatchReportedAbandoned = false;
  matchAnalytics = replayBundle ? null : new MatchAnalyticsAggregator();
  matchStartedAt = new Date().toISOString();
  running = false;
  engine = new BattleEngine({
    seed,
    content,
    arenaId: replayBundle?.replay.arenaId ?? 'basecamp',
    decks: replayBundle?.replay.decks ?? { player: deckSelection.player, bot: activeBotStrategy?.selectedDeck ?? deckSelection.bot },
    diagnosticProfile: 'full',
    matchId: `${replayBundle ? 'replay' : 'battle'}-${seed}-${Date.now().toString(36)}`,
    rules: activeRules,
    ...(activeBotStrategy ? { botStrategy: activeBotStrategy } : {}),
  });
  if (liveBot && activeBotStrategy) {
    const selection = liveBot.selection;
    engine.journal.record({ tick: 0, phase: 'bot', source: 'bot-deck-selector', code: 'BOT_DECK_EVALUATED', payload: { botId: liveBot.definition.id, deckPolicyId: liveBot.definition.deckPolicyId, evaluatedCount: selection.evaluatedCount, topCandidates: selection.topCandidates } });
    engine.journal.record({ tick: 0, phase: 'bot', source: 'bot-deck-selector', code: 'BOT_DECK_SELECTED', cardIds: activeBotStrategy.selectedDeck, payload: { botId: liveBot.definition.id, botVersion: liveBot.definition.version, deckPolicyId: liveBot.definition.deckPolicyId, deckPolicyVersion: liveBot.definition.deckPolicyVersion, deck: activeBotStrategy.selectedDeck, score: activeBotStrategy.deckScore, rationale: activeBotStrategy.rationale } });
    engine.journal.record({ tick: 0, phase: 'bot', source: 'bot-controller', code: 'BOT_STRATEGY_SNAPSHOT_CREATED', payload: activeBotStrategy });
    bot = new BotController(liveBot.definition, { seed, content, journal: engine.journal, playerId: 'bot', strategy: activeBotStrategy });
  } else {
    bot = null;
  }
  await renderer.setArena(engine.arenaDefinition);
  renderer.sync(engine.getView('player'), [], 0);
  replayCommands = replayBundle?.replay.commands ?? null;
  replayIndex = 0;
  paused = false;
  finishing = false;
  selectedCard = null;
  handRenderKey = '';
  lastEvents = [];
  frameDropBurst = null;
  lastFrameDropFlush = performance.now();
  ui.start.classList.remove('open');
  ui.result.classList.remove('open');
  ui.pause.disabled = false;
  ui.pause.textContent = 'ПАУЗА';
  audio.startMusic(engine.state.tick);
  audio.play('match-start', engine.journal, engine.state.tick);
  renderHand();
  syncHud();
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  engine.journal.record({ tick: 0, phase: 'performance', source: 'app-startup', code: 'MATCH_STARTUP_READY', payload: { elapsedMs: performance.now() - startupStartedAt, preloadedCards: new Set(Object.values(CARD_IMAGES)).size } });
  clearStartupRecovery(sessionStorage);
  running = true;
  if (replayBundle) {
    sessionTracker.setMode('gameplay');
    analytics.event('replay_started', { sourceMatchId: replayBundle.manifest.matchId });
  } else {
    sessionTracker.matchStarted();
    analytics.event('match_started', {
      matchId: engine.journal.matchId,
      botId: activeBotStrategy?.botId ?? null,
      arenaId: engine.state.arenaId,
    });
  }
  handRenderKey = '';
  renderHand();
  lastTime = performance.now();
  accumulator = 0;
}

function renderHand(): void {
  if (!engine) return;
  const state = engine.state;
  const player = state.players.player;
  const cooldownActive = state.tick < player.nextCardReadyTick;
  const renderKey = `${player.hand.join(',')}|${player.queue[0]}|${selectedCard ?? ''}|${Math.floor(player.elixirMilli / 1000)}|${state.phase}|${cooldownActive}|${running}|${paused}`;
  if (renderKey === handRenderKey) return;
  handRenderKey = renderKey;
  ui.hand.replaceChildren();
  player.hand.forEach((cardId, index) => {
    const card = content.cards.get(cardId)!;
    const button = document.createElement('button');
    button.className = `card-button${selectedCard === cardId ? ' selected' : ''}`;
    button.dataset.cardId = cardId;
    button.setAttribute('aria-label', `${card.name}, ${card.cost} эликсира`);
    button.disabled = player.elixirMilli < card.cost * 1000 || state.phase === 'countdown' || cooldownActive || !running || paused;
    const image = document.createElement('img');
    image.src = CARD_IMAGES[cardId] ?? '';
    image.alt = card.name;
    image.draggable = false;
    const cost = document.createElement('b');
    cost.textContent = String(card.cost);
    button.append(image, cost);
    button.addEventListener('pointerdown', () => { draggingCard = cardId; });
    button.addEventListener('click', () => {
      draggingCard = null;
      selectCard(cardId);
    });
    button.title = `${index + 1}: ${card.name}`;
    ui.hand.append(button);
  });
  ui.next.src = CARD_IMAGES[player.queue[0]!] ?? '';
}

function selectCard(cardId: CardId | null): void {
  if (!engine || paused || !running || engine.state.phase === 'countdown' || engine.state.tick < engine.state.players.player.nextCardReadyTick) return;
  selectedCard = selectedCard === cardId ? null : cardId;
  if (selectedCard) audio.play('card-pick', engine.journal, engine.state.tick);
  renderer.hidePlacement();
  renderHand();
}

function isPreviewValid(cardId: CardId, point: WorldPoint): boolean {
  return engine?.nearestValidPlacement('player', cardId, point) !== null;
}

function resolvedPlacement(cardId: CardId, point: WorldPoint): WorldPoint {
  return engine?.nearestValidPlacement('player', cardId, point) ?? point;
}

function placementRadius(cardId: CardId): number {
  const card = content.cards.get(cardId);
  if (!card) return 360;
  let radius = 0;
  for (const mechanic of card.mechanics) {
    if (mechanic.type === 'area-projectile') radius = Math.max(radius, content.spells.get(mechanic.spellId)?.radius ?? 0);
    if (mechanic.type === 'spawn-units') for (const group of mechanic.groups) radius = Math.max(radius, group.formationRadius + (content.units.get(group.unitId)?.radius ?? 0));
  }
  return Math.max(300, radius);
}

function playSelected(point: WorldPoint): void {
  if (!engine || !selectedCard || paused) return;
  const command = { type: 'play-card' as const, tick: engine.state.tick, sequence: engine.state.players.player.nextSequence, playerId: 'player' as const, cardId: selectedCard, position: resolvedPlacement(selectedCard, point) };
  const result = engine.enqueue(command);
  if (!result.ok) showToast({ 'insufficient-elixir': 'Недостаточно эликсира', 'card-cooldown': 'Карта ещё не готова', 'invalid-position': 'Нельзя разместить здесь', 'card-not-in-hand': 'Карты уже нет в руке', 'invalid-sequence': 'Команда уже отправлена', 'late-command': 'Команда опоздала', 'future-command': 'Команда слишком ранняя', 'unknown-card': 'Неизвестная карта', 'battle-ended': 'Бой уже завершён' }[result.reason]);
  else selectedCard = null;
  renderer.hidePlacement();
  renderHand();
}

function syncHud(): void {
  if (!engine) return;
  const state = engine.state;
  const remaining = state.phase === 'ended' ? 0 : state.phase === 'normal' ? Math.max(0, activeRules.normalTicks - state.phaseTick) : state.phase === 'overtime' ? Math.max(0, activeRules.overtimeTicks - state.phaseTick) : activeRules.normalTicks;
  const seconds = Math.ceil(remaining / activeRules.tickRate);
  const elixirStage = elixirStageAt(activeRules, state.phase, state.phaseTick);
  ui.timer.textContent = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  ui.phase.textContent = state.phase === 'countdown'
    ? 'ПОДГОТОВКА'
    : state.phase === 'normal'
      ? `ОСНОВНОЕ ВРЕМЯ${elixirStage === 'double' ? ' ×2' : ''}`
      : state.phase === 'overtime'
        ? `ОВЕРТАЙМ ×${elixirStage === 'triple' ? '3' : '2'}`
        : 'ЗАВЕРШЕНО';
  ui.playerCrowns.textContent = String(state.players.player.crowns);
  ui.botCrowns.textContent = String(state.players.bot.crowns);
  ui.elixirValue.textContent = (state.players.player.elixirMilli / 1000).toFixed(1);
  ui.elixirFill.style.width = `${state.players.player.elixirMilli / 100}%`;
  ui.countdown.textContent = state.phase === 'countdown' ? String(Math.max(1, Math.ceil((activeRules.countdownTicks - state.phaseTick) / activeRules.tickRate))) : '';
  if (debugOpen) ui.debugText.textContent = JSON.stringify({ fps: measuredFps, matchId: engine.journal.matchId, seed: state.seed, tick: state.tick, phase: state.phase, checksum: engine.checksum(), entities: state.entities.filter((item) => !item.removed).length, projectiles: state.projectiles.length, records: engine.journal.records.length, droppedRecords: engine.journal.droppedRecords, selectedCard, selectedEntity: selectedEntityId === null ? null : state.entities.find((item) => item.id === selectedEntityId) ?? null }, null, 2);
}

async function finishMatch(): Promise<void> {
  if (!engine || finishing) return;
  finishing = true;
  running = false;
  audio.stopMusic();
  audio.play('match-end', engine.journal, engine.state.tick);
  flushFrameDropBurst(performance.now());
  await audio.flushDiagnostics(engine.journal);
  let checksumVerified = false;
  try {
    const replayed = playReplay(engine.replay, content, activeRules);
    checksumVerified = replayed.checksum() === engine.checksum();
    if (!checksumVerified) engine.journal.record({ tick: engine.state.tick, phase: 'simulation', source: 'replay-verifier', level: 'error', code: 'DET_CHECKSUM_MISMATCH', payload: { live: engine.checksum(), replay: replayed.checksum() } });
  } catch (error) {
    engine.journal.record({ tick: engine.state.tick, phase: 'simulation', source: 'replay-verifier', level: 'error', code: 'DET_CHECKSUM_MISMATCH', payload: { error: String(error) } });
  }
  if (learningEligible && activeBotStrategy) {
    try {
      const features = extractPlayerMatchFeatures(engine.journal.matchId, engine.replay, content);
      engine.journal.record({ tick: engine.state.tick, phase: 'bot', source: 'player-style-extractor', code: 'PLAYER_MODEL_EXTRACTED', payload: { matchId: features.matchId, plays: features.plays, averageCostMilli: features.averageCostMilli, style: features.style } });
      const training = trainPlayerLearningModel(playerLearningModel, features, content);
      if (training.updated) {
        await savePlayerLearningModel(training.model);
        playerLearningModel = training.model;
        engine.journal.record({ tick: engine.state.tick, phase: 'bot', source: 'player-style-trainer', code: 'PLAYER_MODEL_UPDATED', payload: { modelVersion: playerLearningModel.modelVersion, revision: playerLearningModel.revision, matchCount: playerLearningModel.matchCount, confidence: playerLearningModel.confidence, modelHash: hashPlayerModel(playerLearningModel), style: playerLearningModel.style } });
        renderBotConfiguration();
      } else {
        engine.journal.record({ tick: engine.state.tick, phase: 'bot', source: 'player-style-trainer', code: 'PLAYER_MODEL_SKIPPED', payload: { reason: 'duplicate-match', matchId: features.matchId } });
      }
    } catch (error) {
      engine.journal.record({ tick: engine.state.tick, phase: 'bot', source: 'player-style-trainer', level: 'warn', code: 'PLAYER_MODEL_SKIPPED', payload: { reason: 'training-failed', error: String(error) } });
    }
  }
  lastBundle = createDiagnosticBundle({ journal: engine.journal, replay: engine.replay, checkpoints: engine.checkpoints, result: engine.state.result, engineVersion: ENGINE_VERSION, contentVersion: content.version, arenaId: engine.state.arenaId, seed: engine.state.seed });
  try { await saveDiagnosticBundle(lastBundle, settings.uploadDiagnosticLogs && !currentMatchIsReplay); }
  catch (error) {
    engine.journal.sinkErrors += 1;
    lastBundle = createDiagnosticBundle({ journal: engine.journal, replay: engine.replay, checkpoints: engine.checkpoints, result: engine.state.result, engineVersion: ENGINE_VERSION, contentVersion: content.version, arenaId: engine.state.arenaId, seed: engine.state.seed });
    showToast(`Лог не сохранён: ${String(error)}`);
  }
  if (currentMatchIsReplay) {
    sessionTracker.setMode('menu');
  } else if (lastBundle.summary.result) {
    sessionTracker.matchCompleted();
    const aggregate = matchAnalytics?.snapshot() ?? { commandsAccepted: 0, commandsRejected: 0, phases: [], cardStats: [] };
    analytics.match({
      schemaVersion: 1,
      matchId: lastBundle.manifest.matchId,
      anonymousId: analyticsIdentity.anonymousId,
      sessionId: analyticsIdentity.sessionId,
      startedAt: matchStartedAt,
      completedAt: new Date().toISOString(),
      arenaId: lastBundle.manifest.arenaId,
      rulesetId: lastBundle.manifest.rulesetId,
      engineVersion: lastBundle.manifest.engineVersion,
      contentVersion: lastBundle.manifest.contentVersion,
      botId: activeBotStrategy?.botId ?? null,
      botVersion: activeBotStrategy?.botVersion ?? null,
      durationTicks: lastBundle.summary.result.tick,
      tickRate: lastBundle.replay.rules.tickRate,
      result: lastBundle.summary.result,
      commandsAccepted: aggregate.commandsAccepted,
      commandsRejected: aggregate.commandsRejected,
      phases: aggregate.phases,
      cardStats: aggregate.cardStats,
      diagnosticMetrics: lastBundle.metrics,
      diagnosticCounts: {
        critical: lastBundle.summary.critical,
        errors: lastBundle.summary.errors,
        warnings: lastBundle.summary.warnings,
      },
      topFindings: lastBundle.findings.slice(0, 10).map(({ ruleId, severity }) => ({ ruleId, severity })),
      checksumVerified,
    });
    analytics.event('match_completed', {
      matchId: lastBundle.manifest.matchId,
      winner: lastBundle.summary.result.winner,
      durationSeconds: lastBundle.summary.result.tick / lastBundle.replay.rules.tickRate,
    });
    analytics.session(sessionTracker.snapshot());
  }
  void diagnosticUploader.process();
  showResult(lastBundle);
}

function showResult(bundle: MatchDiagnosticBundle): void {
  const result = bundle.summary.result;
  if (!result) { showToast('Сохранённый бой не содержит результата'); return; }
  const replayMode = engine?.journal.matchId.startsWith('replay') ?? bundle.manifest.matchId.startsWith('replay');
  const label = result.winner === 'draw' ? 'НИЧЬЯ' : result.winner === 'player' ? 'ПОБЕДА' : 'ПОРАЖЕНИЕ';
  ui.resultBadge.textContent = replayMode ? `REPLAY · ${label}` : label;
  ui.resultTitle.textContent = result.reason === 'king-destroyed'
    ? 'КОРОЛЬ ПОВЕРЖЕН'
    : result.reason === 'sudden-death'
      ? 'ПОБЕДА В ОВЕРТАЙМЕ'
      : result.reason === 'tiebreak'
        ? 'ТАЙБРЕЙК'
        : 'БОЙ ЗАВЕРШЁН';
  ui.resultSummary.textContent = `${result.crowns.player}:${result.crowns.bot} · ${(result.tick / bundle.replay.rules.tickRate).toFixed(1)} сек · checksum ${engine?.checksum() ?? bundle.checkpoints.at(-1)?.checksum ?? 'нет'}`;
  ui.diagnosticCounts.textContent = `${bundle.metrics.records} записей · ${bundle.summary.critical} critical · ${bundle.summary.errors} errors · ${bundle.summary.warnings} warnings`;
  ui.findings.replaceChildren();
  for (const finding of bundle.findings.slice(0, 30)) {
    const item = document.createElement('li');
    item.className = finding.severity;
    item.textContent = `[${finding.ruleId}] tick ${finding.tickFrom}: ${finding.message}`;
    ui.findings.append(item);
  }
  if (!bundle.findings.length) {
    const item = document.createElement('li');
    item.textContent = 'Нарушения не обнаружены';
    ui.findings.append(item);
  }
  ui.export.disabled = false;
  ui.replay.disabled = false;
  deleteResultLogArmedUntil = 0;
  ui.deleteLog.disabled = false;
  ui.deleteLog.textContent = 'УДАЛИТЬ ЛОГ';
  ui.result.classList.add('open');
  ui.pause.disabled = true;
}

let lastTime = performance.now();
let accumulator = 0;
function flushFrameDropBurst(now: number): void {
  if (!engine || !frameDropBurst) return;
  engine.journal.record({
    tick: frameDropBurst.lastTick,
    phase: 'performance',
    source: 'app-frame',
    level: 'warn',
    code: 'FRAME_DROP_BURST',
    payload: {
      ...frameDropBurst,
      averageFrameMs: frameDropBurst.totalFrameMs / frameDropBurst.count,
      phase: engine.state.phase,
      entities: engine.state.entities.filter((entity) => !entity.removed).length,
      projectiles: engine.state.projectiles.length,
      visibilityState: document.visibilityState,
    },
  });
  frameDropBurst = null;
  lastFrameDropFlush = now;
}

function frame(now: number): void {
  const rawDelta = (now - lastTime) / 1000;
  // Keep the production catch-up cap conservative, but let the explicit smoke-test
  // mode track wall time on slow software-rendered CI devices.
  const delta = Math.min(fastMode ? 1 : 0.1, rawDelta);
  lastTime = now;
  fpsFrames += 1;
  if (now - fpsWindowStart >= 500) { measuredFps = Math.round((fpsFrames * 1000) / (now - fpsWindowStart)); fpsFrames = 0; fpsWindowStart = now; }
  if (running && engine && rawDelta > 0.08 && document.visibilityState === 'visible') {
    const frameMs = rawDelta * 1000;
    frameDropBurst ??= { count: 0, maxFrameMs: 0, totalFrameMs: 0, firstTick: engine.state.tick, lastTick: engine.state.tick };
    frameDropBurst.count += 1;
    frameDropBurst.maxFrameMs = Math.max(frameDropBurst.maxFrameMs, frameMs);
    frameDropBurst.totalFrameMs += frameMs;
    frameDropBurst.lastTick = engine.state.tick;
  }
  if (frameDropBurst && now - lastFrameDropFlush >= 2_000) flushFrameDropBurst(now);
  if (running && !paused && engine) {
    accumulator += delta;
    const frameEvents: BattleEvent[] = [];
    const stepSeconds = 1 / activeRules.tickRate;
    while (accumulator >= stepSeconds) {
      if (replayCommands) {
        while (replayIndex < replayCommands.length && replayCommands[replayIndex]!.tick <= engine.state.tick) engine.enqueue(replayCommands[replayIndex++]!);
      } else {
        const command = bot?.decide(engine.getView('bot'));
        if (command) engine.enqueue(command);
      }
      frameEvents.push(...engine.step());
      accumulator -= stepSeconds;
      if (engine.state.result) break;
    }
    lastEvents = frameEvents;
    matchAnalytics?.consume(frameEvents);
    renderer.sync(engine.getView('player'), lastEvents, delta);
    audio.handle(lastEvents, engine.journal);
    renderHand();
    syncHud();
    if (engine.state.result) void finishMatch();
  }
  if (toastTimer > 0) { toastTimer -= delta; if (toastTimer <= 0) ui.toast.classList.remove('show'); }
  requestAnimationFrame(frame);
}

async function boot(): Promise<void> {
  // The deck editor must stay independent from the heavier on-demand PixiJS renderer.
  audio.preload();
  persistSettings();
  renderDeckBuilder();
  try {
    const loadedModel = await loadPlayerLearningModel(content.version);
    playerLearningModel = loadedModel.model;
    if (loadedModel.repaired) showToast('Модель обучения бота была несовместима и сброшена');
  } catch (error) {
    playerLearningModel = createEmptyPlayerLearningModel(content.version);
    showToast(`Обучение бота недоступно: ${String(error)}`);
  }
  renderBotConfiguration();
  if (parsedDecks.repaired) showToast('Сохранённая колода была повреждена или устарела — восстановлены стандартные 8 карт');
  if (parsedBotPreferences.repaired) showToast('Неизвестный вариант бота заменён адаптивным');
  try {
    lastBundle = await loadLatestDiagnosticBundle();
    ui.lastBattle.hidden = !lastBundle;
  } catch (error) {
    ui.lastBattle.hidden = true;
    showToast(`Последний лог повреждён или недоступен: ${String(error)}`);
  }
  analytics.event('app_loaded', {
    engineVersion: ENGINE_VERSION,
    contentVersion: content.version,
    language: navigator.language,
  });
  analytics.session(sessionTracker.snapshot());
  void diagnosticUploader.process();
  requestAnimationFrame(frame);
  const startupRetry = consumeStartupRetry(sessionStorage);
  if (startupRetry === 'live') void startMatch();
  else if (startupRetry === 'replay') {
    clearStartupRecovery(sessionStorage);
    showToast('Игра обновлена — нажмите REPLAY ещё раз');
    toastTimer = 6;
  }
}

const gameHost = $('#gameHost');
gameHost.addEventListener('pointermove', (event) => {
  if (!engine || !selectedCard) return;
  const point = renderer.screenToWorld(event.clientX, event.clientY);
  renderer.showPlacement(resolvedPlacement(selectedCard, point), isPreviewValid(selectedCard, point), placementRadius(selectedCard));
});
gameHost.addEventListener('pointerup', (event) => {
  if (draggingCard) return;
  const point = renderer.screenToWorld(event.clientX, event.clientY);
  if (selectedCard) playSelected(point);
  else if (engine && debugOpen) {
    const nearest = engine.getView('player').entities.slice().sort((left, right) => Math.hypot(left.x - point.x, left.y - point.y) - Math.hypot(right.x - point.x, right.y - point.y) || left.id - right.id)[0];
    selectedEntityId = nearest && Math.hypot(nearest.x - point.x, nearest.y - point.y) <= 900 ? nearest.id : null;
    syncHud();
  }
});
gameHost.addEventListener('pointerleave', () => renderer.hidePlacement());
document.addEventListener('pointermove', (event) => {
  if (!engine || !draggingCard) return;
  const rect = gameHost.getBoundingClientRect();
  if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) return;
  const point = renderer.screenToWorld(event.clientX, event.clientY);
  renderer.showPlacement(resolvedPlacement(draggingCard, point), isPreviewValid(draggingCard, point), placementRadius(draggingCard));
});
document.addEventListener('pointerup', (event) => {
  if (!engine || !draggingCard) return;
  const cardId = draggingCard;
  draggingCard = null;
  if (event.target instanceof Element && event.target.closest('.card-button')) return;
  const rect = gameHost.getBoundingClientRect();
  if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) return;
  selectedCard = cardId;
  playSelected(renderer.screenToWorld(event.clientX, event.clientY));
});

window.addEventListener('keydown', (event) => {
  if (!engine) return;
  if (event.key >= '1' && event.key <= '4') selectCard(engine.state.players.player.hand[Number(event.key) - 1] ?? null);
  if (event.key === 'Escape') selectCard(null);
  if (event.code === 'Space') { event.preventDefault(); togglePause(); }
});

function togglePause(): void {
  if (!running) return;
  paused = !paused;
  sessionTracker.setMode(paused ? 'paused' : 'gameplay');
  ui.pause.textContent = paused ? 'ПРОДОЛЖИТЬ' : 'ПАУЗА';
  if (paused) audio.stopMusic(); else audio.startMusic(engine?.state.tick ?? 0);
  renderHand();
}

ui.playerNameInput.addEventListener('input', () => {
  ui.playerNameLabel.textContent = normalizePlayerName(ui.playerNameInput.value);
});
ui.playerNameInput.addEventListener('blur', () => { commitPlayerName(); });
ui.play.addEventListener('click', () => {
  analytics.event('match_start_clicked', { source: 'home' });
  void startMatch();
});
ui.playerDeckTab.addEventListener('click', () => { deckEditorOwner = 'player'; selectedDeckSlot = 0; renderDeckBuilder(); });
ui.botDeckTab.addEventListener('click', () => { deckEditorOwner = 'bot'; selectedDeckSlot = 0; renderDeckBuilder(); });
ui.botType.addEventListener('change', () => {
  botPreferences = { schemaVersion: 1, botId: ui.botType.value };
  persistBotPreferences();
  renderBotConfiguration();
});
ui.resetBotLearning.addEventListener('click', async () => {
  if (playerLearningModel.matchCount === 0) return;
  try {
    await clearPlayerLearningModel();
    playerLearningModel = createEmptyPlayerLearningModel(content.version);
    renderBotConfiguration();
    showToast('Обучение бота сброшено');
  } catch (error) {
    showToast(`Не удалось сбросить обучение: ${String(error)}`);
  }
});
$('#resetDeckButton').addEventListener('click', () => {
  deckSelection = { ...deckSelection, [deckEditorOwner]: [...defaultDecks[deckEditorOwner]] };
  selectedDeckSlot = 0;
  persistDeckSelection();
  renderDeckBuilder();
});
ui.lastBattle.addEventListener('click', () => {
  if (!lastBundle) return;
  ui.start.classList.remove('open');
  showResult(lastBundle);
});
$('#startSettingsButton').addEventListener('click', () => { resumeAfterSettings = false; ui.settings.classList.add('open'); });
$('#againButton').addEventListener('click', () => {
  analytics.event('match_start_clicked', { source: 'result' });
  void startMatch();
});
$('#replayButton').addEventListener('click', () => { if (lastBundle) void startMatch(lastBundle); });
$('#exportButton').addEventListener('click', () => {
  if (!lastBundle) return;
  downloadDiagnosticBundle(lastBundle);
  analytics.event('diagnostics_exported', { matchId: lastBundle.manifest.matchId });
});
ui.deleteLog.addEventListener('click', async () => {
  const bundle = lastBundle;
  if (!bundle) return;
  if (Date.now() > deleteResultLogArmedUntil) {
    deleteResultLogArmedUntil = Date.now() + 4_000;
    ui.deleteLog.textContent = 'НАЖМИ ЕЩЁ РАЗ';
    window.setTimeout(() => {
      if (Date.now() >= deleteResultLogArmedUntil && !ui.deleteLog.disabled) ui.deleteLog.textContent = 'УДАЛИТЬ ЛОГ';
    }, 4_050);
    return;
  }
  deleteResultLogArmedUntil = 0;
  ui.deleteLog.disabled = true;
  try {
    await deleteDiagnosticBundle(bundle.manifest.matchId);
    if (lastBundle?.manifest.matchId === bundle.manifest.matchId) lastBundle = null;
    ui.lastBattle.hidden = !lastBundle;
    ui.export.disabled = true;
    ui.replay.disabled = true;
    ui.deleteLog.textContent = 'ЛОГ УДАЛЁН';
    showToast('Лог этого боя удалён');
  } catch (error) {
    ui.deleteLog.textContent = 'ОШИБКА УДАЛЕНИЯ';
    showToast(`Не удалось удалить лог: ${String(error)}`);
    window.setTimeout(() => {
      ui.deleteLog.disabled = false;
      ui.deleteLog.textContent = 'УДАЛИТЬ ЛОГ';
    }, 1_800);
  }
});
$('#homeButton').addEventListener('click', () => { ui.result.classList.remove('open'); ui.start.classList.add('open'); ui.lastBattle.hidden = !lastBundle; });
ui.pause.addEventListener('click', togglePause);
$('#debugButton').addEventListener('click', () => { debugOpen = !debugOpen; ui.debugPanel.hidden = !debugOpen; syncHud(); });
$('#settingsButton').addEventListener('click', () => {
  resumeAfterSettings = running && !paused;
  if (resumeAfterSettings) togglePause();
  ui.settings.classList.add('open');
});
$('#closeSettings').addEventListener('click', () => {
  ui.settings.classList.remove('open');
  persistSettings();
  if (resumeAfterSettings && running && paused) togglePause();
  resumeAfterSettings = false;
});
const musicVolume = $('#musicVolume') as HTMLInputElement;
const sfxVolume = $('#sfxVolume') as HTMLInputElement;
const muteSetting = $('#muteSetting') as HTMLInputElement;
const motionSetting = $('#motionSetting') as HTMLInputElement;
const qualitySetting = $('#qualitySetting') as HTMLSelectElement;
const metricsSetting = $('#metricsSetting') as HTMLInputElement;
const logUploadSetting = $('#logUploadSetting') as HTMLInputElement;
const deleteServerLogsButton = $('#deleteServerLogsButton') as HTMLButtonElement;
const clearLogsButton = $('#clearLogsButton') as HTMLButtonElement;
syncPlayerName();
musicVolume.value = String(settings.musicVolume * 100); sfxVolume.value = String(settings.sfxVolume * 100); muteSetting.checked = settings.muted; motionSetting.checked = settings.reducedMotion; qualitySetting.value = settings.quality; metricsSetting.checked = settings.shareAnonymousMetrics; logUploadSetting.checked = settings.uploadDiagnosticLogs;
musicVolume.addEventListener('input', () => { settings.musicVolume = Number(musicVolume.value) / 100; persistSettings(); });
sfxVolume.addEventListener('input', () => { settings.sfxVolume = Number(sfxVolume.value) / 100; persistSettings(); });
muteSetting.addEventListener('change', () => { settings.muted = muteSetting.checked; persistSettings(); });
motionSetting.addEventListener('change', () => { settings.reducedMotion = motionSetting.checked; persistSettings(); });
qualitySetting.addEventListener('change', () => { settings.quality = qualitySetting.value as AppSettings['quality']; persistSettings(); });
metricsSetting.addEventListener('change', () => {
  settings.shareAnonymousMetrics = metricsSetting.checked;
  persistSettings();
  if (settings.shareAnonymousMetrics) {
    analytics.event('app_loaded', { source: 'consent-enabled' });
    analytics.session(sessionTracker.snapshot());
  }
});
logUploadSetting.addEventListener('change', async () => {
  settings.uploadDiagnosticLogs = logUploadSetting.checked;
  persistSettings();
  if (settings.uploadDiagnosticLogs) {
    void diagnosticUploader.process();
    showToast('Загрузка полных логов включена для следующих завершённых боёв');
  } else {
    try { await clearDiagnosticUploadQueue(); } catch { /* The preference still prevents any network upload. */ }
    showToast('Загрузка отключена; локальное обучение бота продолжит работать');
  }
});
deleteServerLogsButton.addEventListener('click', async () => {
  if (Date.now() > deleteServerLogsArmedUntil) {
    deleteServerLogsArmedUntil = Date.now() + 4_000;
    deleteServerLogsButton.textContent = 'НАЖМИ ЕЩЁ РАЗ ДЛЯ УДАЛЕНИЯ';
    window.setTimeout(() => {
      if (Date.now() >= deleteServerLogsArmedUntil && !deleteServerLogsButton.disabled) deleteServerLogsButton.textContent = 'УДАЛИТЬ МОИ ЗАГРУЖЕННЫЕ ЛОГИ';
    }, 4_050);
    return;
  }
  deleteServerLogsArmedUntil = 0;
  deleteServerLogsButton.disabled = true;
  try {
    const response = await fetch('/api/diagnostics-delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(analyticsIdentity),
    });
    if (!response.ok && response.status !== 403) throw new Error(`HTTP ${response.status}`);
    deleteServerLogsButton.textContent = response.ok ? 'ЗАГРУЖЕННЫЕ ЛОГИ УДАЛЕНЫ' : 'НА СЕРВЕРЕ НЕТ МОИХ ЛОГОВ';
    showToast(response.ok ? 'Загруженные полные логи удалены с сервера' : 'Загруженные логи не найдены');
  } catch (error) {
    deleteServerLogsButton.textContent = 'ОШИБКА УДАЛЕНИЯ';
    showToast(`Не удалось удалить серверные логи: ${String(error)}`);
  } finally {
    window.setTimeout(() => {
      deleteServerLogsButton.disabled = false;
      deleteServerLogsButton.textContent = 'УДАЛИТЬ МОИ ЗАГРУЖЕННЫЕ ЛОГИ';
    }, 1_800);
  }
});
clearLogsButton.addEventListener('click', async () => {
  if (Date.now() > clearLogsArmedUntil) {
    clearLogsArmedUntil = Date.now() + 4_000;
    clearLogsButton.textContent = 'НАЖМИ ЕЩЁ РАЗ ДЛЯ ОЧИСТКИ';
    window.setTimeout(() => {
      if (Date.now() >= clearLogsArmedUntil) clearLogsButton.textContent = 'СТЕРЕТЬ СОХРАНЁННЫЕ ЛОГИ';
    }, 4_050);
    return;
  }
  clearLogsArmedUntil = 0;
  clearLogsButton.disabled = true;
  try {
    await clearDiagnosticHistory();
    lastBundle = null;
    ui.lastBattle.hidden = true;
    ui.export.disabled = true;
    ui.replay.disabled = true;
    clearLogsButton.textContent = 'ЛОГИ УДАЛЕНЫ';
    showToast(running ? 'История очищена; текущий бой будет сохранён после завершения' : 'Сохранённые логи удалены');
  } catch (error) {
    clearLogsButton.textContent = 'ОШИБКА ОЧИСТКИ';
    showToast(`Не удалось удалить логи: ${String(error)}`);
  } finally {
    window.setTimeout(() => { clearLogsButton.disabled = false; clearLogsButton.textContent = 'СТЕРЕТЬ СОХРАНЁННЫЕ ЛОГИ'; }, 1_800);
  }
});

document.addEventListener('pointerdown', () => sessionTracker.noteInteraction(), { passive: true });
document.addEventListener('keydown', () => sessionTracker.noteInteraction(), { passive: true });
document.addEventListener('visibilitychange', () => {
  if (document.hidden && running && !paused) togglePause();
  sessionTracker.setVisible(!document.hidden);
});
window.addEventListener('online', () => { void diagnosticUploader.process(); });
window.addEventListener('pagehide', () => {
  if (running && !currentMatchIsReplay && !currentMatchReportedAbandoned) {
    currentMatchReportedAbandoned = true;
    sessionTracker.matchAbandoned();
    analytics.event('match_abandoned', { matchId: engine?.journal.matchId ?? null });
  }
  analytics.session(sessionTracker.snapshot(), true);
});
window.setInterval(() => analytics.session(sessionTracker.snapshot()), 5 * 60_000);
void boot();
